import React, { useState } from "react";
import { useGetStarControlUsersQuery } from "../api";
import { StarControlParams, StarControlUser } from "../api/starControl";

const StarControlPage: React.FC = () => {
    // Состояния фильтров
    const [filters, setFilters] = useState<StarControlParams>({
        page: 1,
        per_page: 20,
        days_ahead: undefined,
        show_only_expiring: false,
        show_only_low_balance: false,
        sponsor_id: undefined,
    });

    // Запрос данных
    const { data, isLoading, error } = useGetStarControlUsersQuery(filters);

    const handleFilterChange = (key: keyof StarControlParams, value: any) => {
        setFilters((prev) => ({
            ...prev,
            [key]: value,
            page: 1, // Сброс на первую страницу при изменении фильтров
        }));
    };

    const handlePageChange = (page: number) => {
        setFilters((prev) => ({
            ...prev,
            page,
        }));
    };

    const resetFilters = () => {
        setFilters({
            page: 1,
            per_page: 20,
            days_ahead: undefined,
            show_only_expiring: false,
            show_only_low_balance: false,
            sponsor_id: undefined,
        });
    };

    const formatDate = (dateString: string) => {
        try {
            return new Date(dateString).toLocaleDateString("ru-RU");
        } catch {
            return dateString;
        }
    };

    const getBalanceStatusColor = (total: number) => {
        if (total < 5) return "text-red-600 dark:text-red-400";
        if (total < 10) return "text-orange-600 dark:text-orange-400";
        return "text-green-600 dark:text-green-400";
    };

    const getExpiryStatusColor = (daysUntilExpiry: number) => {
        if (daysUntilExpiry < 0) return "text-red-600 dark:text-red-400";
        if (daysUntilExpiry < 2) return "text-orange-600 dark:text-orange-400";
        return "text-gray-600 dark:text-gray-400";
    };

    const getExpiryStatusText = (daysUntilExpiry: number) => {
        if (daysUntilExpiry < 0)
            return `${Math.abs(daysUntilExpiry).toFixed(1)} дн. назад`;
        if (daysUntilExpiry === 0) return "сегодня";
        if (daysUntilExpiry === 1) return "завтра";
        return `через ${daysUntilExpiry.toFixed(1)} дн.`;
    };

    const renderPagination = () => {
        if (!data?.meta || data.meta.last_page <= 1) return null;

        const { current_page, last_page } = data.meta;
        const pages = [];

        // Показываем максимум 7 страниц
        const startPage = Math.max(1, current_page - 3);
        const endPage = Math.min(last_page, startPage + 6);

        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <button
                    key={i}
                    onClick={() => handlePageChange(i)}
                    className={`px-3 py-1 mx-1 rounded ${
                        i === current_page
                            ? "bg-blue-600 text-white"
                            : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                    }`}
                >
                    {i}
                </button>
            );
        }

        return (
            <div className="flex justify-center items-center mt-6 space-x-2">
                <button
                    onClick={() => handlePageChange(current_page - 1)}
                    disabled={current_page <= 1}
                    className="px-3 py-1 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50"
                >
                    ←
                </button>
                {pages}
                <button
                    onClick={() => handlePageChange(current_page + 1)}
                    disabled={current_page >= last_page}
                    className="px-3 py-1 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50"
                >
                    →
                </button>
            </div>
        );
    };

    return (
        <div className="container mx-auto px-4 py-6">
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-200">
                    Контроль Звёздная
                </h1>
                <p className="text-gray-600 dark:text-gray-400 mt-2">
                    Мониторинг пользователей программы "Звёздная", у которых
                    истекает срок оплаты и недостаточно средств
                </p>
            </div>

            {/* Фильтры */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
                <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-200">
                    Фильтры
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Дней до истечения
                        </label>
                        <input
                            type="number"
                            value={filters.days_ahead || ""}
                            onChange={(e) =>
                                handleFilterChange(
                                    "days_ahead",
                                    parseInt(e.target.value) || undefined
                                )
                            }
                            disabled={!filters.show_only_expiring}
                            min="1"
                            max="30"
                            className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 disabled:opacity-50"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            ID спонсора
                        </label>
                        <input
                            type="number"
                            value={filters.sponsor_id || ""}
                            onChange={(e) =>
                                handleFilterChange(
                                    "sponsor_id",
                                    parseInt(e.target.value) || undefined
                                )
                            }
                            placeholder="Введите ID спонсора (необязательно)"
                            className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200"
                        />
                        <div className="mt-3">
                            <label className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    checked={
                                        filters.show_only_expiring || false
                                    }
                                    onChange={(e) =>
                                        handleFilterChange(
                                            "show_only_expiring",
                                            e.target.checked
                                        )
                                    }
                                    className="rounded"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                    Только истекающие
                                </span>
                            </label>
                            <label className="flex items-center space-x-2 mt-2">
                                <input
                                    type="checkbox"
                                    checked={
                                        filters.show_only_low_balance || false
                                    }
                                    onChange={(e) =>
                                        handleFilterChange(
                                            "show_only_low_balance",
                                            e.target.checked
                                        )
                                    }
                                    className="rounded"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                    Только с низким балансом (&lt; $10)
                                </span>
                            </label>
                        </div>
                    </div>

                    <div className="flex items-end space-x-2">
                        <button
                            onClick={() =>
                                handleFilterChange(
                                    "per_page",
                                    filters.per_page === 20 ? 50 : 20
                                )
                            }
                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                            Применить
                        </button>
                        <button
                            onClick={resetFilters}
                            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                        >
                            Сбросить
                        </button>
                    </div>
                </div>
            </div>

            {/* Таблица */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
                {isLoading ? (
                    <div className="text-center py-8">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <p className="mt-2 text-gray-600 dark:text-gray-400">
                            Загрузка...
                        </p>
                    </div>
                ) : error ? (
                    <div className="text-center py-8 text-red-600 dark:text-red-400">
                        Ошибка загрузки данных
                    </div>
                ) : !data?.data?.length ? (
                    <div className="text-center py-8 text-gray-600 dark:text-gray-400">
                        Данные не найдены
                    </div>
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Фото
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            ID
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            ФИО
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Ранг
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Регистрация
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Звёздная с
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Оплата до
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Команда
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Баланс
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Статус
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                    {data.data.map((user: StarControlUser) => (
                                        <tr
                                            key={user.user_id}
                                            className={`${
                                                user.is_blocked
                                                    ? "bg-red-50 dark:bg-red-900/20"
                                                    : "hover:bg-gray-50 dark:hover:bg-gray-700"
                                            }`}
                                        >
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <img
                                                    src={user.photo}
                                                    alt={user.full_name}
                                                    className="h-10 w-10 rounded-full object-cover"
                                                    onError={(e) => {
                                                        const target =
                                                            e.target as HTMLImageElement;
                                                        target.src =
                                                            "/images/no_photo.png";
                                                    }}
                                                />
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-200">
                                                {user.ref_link}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">
                                                {user.full_name}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">
                                                {user.rank}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">
                                                {formatDate(user.created_at)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">
                                                {user.star_register_date
                                                    ? formatDate(
                                                          user.star_register_date
                                                      )
                                                    : "-"}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm">
                                                    <div className="text-gray-900 dark:text-gray-200">
                                                        {user.star_pay_date
                                                            ? formatDate(
                                                                  user.star_pay_date
                                                              )
                                                            : "-"}
                                                    </div>
                                                    <div
                                                        className={`text-xs ${getExpiryStatusColor(
                                                            user.days_until_expiry
                                                        )}`}
                                                    >
                                                        {getExpiryStatusText(
                                                            user.days_until_expiry
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">
                                                {user.star_team_total}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm">
                                                    <div
                                                        className={`font-medium ${getBalanceStatusColor(
                                                            user.balances.total
                                                        )}`}
                                                    >
                                                        $
                                                        {user.balances.total.toFixed(
                                                            2
                                                        )}
                                                    </div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                                        Main: $
                                                        {user.balances.main_wallet.toFixed(
                                                            2
                                                        )}{" "}
                                                        | USDT: $
                                                        {user.balances.usdt_wallet.toFixed(
                                                            2
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                <span
                                                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                                        user.is_blocked
                                                            ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                                                            : "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                                    }`}
                                                >
                                                    {user.is_blocked
                                                        ? "Заблокирован"
                                                        : "Активен"}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Пагинация */}
                        {renderPagination()}

                        {/* Статистика */}
                        {data.meta && (
                            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 text-sm text-gray-600 dark:text-gray-400">
                                Показано страница {data.meta.current_page} из{" "}
                                {data.meta.last_page} (всего записей:{" "}
                                {data.meta.total})
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default StarControlPage;
